import React from 'react';
import ClickableHelpIcon from 'components/pure/utils/ClickableHelpIcon';
import './ReviewTypeQuestions.styl';
import Button from '@material-ui/core/Button';
import ReviewTypeQuestionRow from 'components/pure/admin/ReviewTypeQuestionRow';

const typeHelp = () =>
  `Select the type of this question. This determines how the answer field will be presented to the coach during the review meeting.`;
const titleHelp = () =>
  `Type in text for the question. Typically this would be in the form of a question, concluding with a question mark ("Did the rep meet their goals?") but it could also be a simple statement ("Goal review.")`;
const optionalHelp = () =>
  `Click to make this question optional; leaving it unselected will require the question to be answered.`;
const whoHelp = () =>
  `If you select "Rep" here, they'll be asked to answer questions ahead of your meeting.`;
const notesHelp = () =>
  `Optional. Supply supplementary notes for the coach about the question. These will be shown on the review page when the review is in progress, but won't be shown on historical review pages.`;
const weightHelp = () =>
  `Score weighting must total 100% between the questions and the call(s). Answers to questions will be weighted as configured below, for a total score out of 100. If the review counts towards the skill being coached, this score will be recorded in the historical plan progress.`;

const headers = [
  ['', null, 'reorder-header'],
  ['Question Type', typeHelp, 'question-type-header'],
  ['Question Title', titleHelp, 'title-header'],
  ['Optional', optionalHelp, 'optional-header'],
  ['Who', whoHelp, 'who-header'],
  ['Coach Notes', notesHelp, 'notes-header'],
  ['Score Weighting', weightHelp, 'weight-header'],
];

export default class ReviewTypeQuestions extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {dragIndex: 0};

    this.addQuestion = this.addQuestion.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onChangeQuestionType = this.onChangeQuestionType.bind(this);
    this.renderHeader = this.renderHeader.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDrop = this.onDrop.bind(this);
    this.deleteQuestion = this.deleteQuestion.bind(this);
  }

  onDragStart(e, dragIndex, ref) {
    this.setState({dragIndex});
    e.dataTransfer.setDragImage(ref, 0, 0);
    e.dataTransfer.effectAllowed = 'move';
  }

  onDragOver(e) {
    e.preventDefault();
  }

  onDrop(e, dropIndex) {
    if (e.dataTransfer.effectAllowed === 'move') {
      e.preventDefault();

      this.props.onMoveQuestion(this.state.dragIndex, dropIndex);
    }
  }

  addQuestion() {
    this.props.onAddNewQuestion();
  }

  onChange(index, key, change) {
    const questions = this.props.questions.setIn([index, key], change);
    this.props.onChange(questions);
  }

  onChangeQuestionType(index, type) {
    const updatedQuestion = this.props.questions.get(index).set('type', type);

    const reweighted = updatedQuestion.isText()
      ? updatedQuestion.set('weight', 0)
      : updatedQuestion;

    this.props.onChange(this.props.questions.set(index, reweighted));
    setImmediate(() => this.props.onWeightBlur());
  }

  onWeightBlur(question, idx) {
    this.props.onWeightBlur(question, idx);
  }

  deleteQuestion(index) {
    this.props.onDeleteQuestion(index);
  }

  renderHeader([content, help, className], i) {
    return (
      <th className={className} key={`header${i}`}>
        <div className="header-content">
          <div className="header-text">{content}</div>
          {help && <ClickableHelpIcon text={help} />}
        </div>
      </th>
    );
  }

  render() {
    const {questions, errors, showErrors, questionsToBlink, children} = this.props;

    return (
      <div className="session-type-questions">
        <h4>Questions</h4>
        <table>
          <thead>
            <tr>{headers.map(this.renderHeader)}</tr>
          </thead>
          <tbody>
            {children}
            {questions.isEmpty() ? (
              <tr>
                <td colSpan={6}>
                  <p className="question-table-empty">
                    This meeting type has no questions yet. Click Add Question to create new ones.
                  </p>
                </td>
              </tr>
            ) : (
              questions.map((question, i) => (
                <ReviewTypeQuestionRow
                  key={`question-${i}`}
                  question={question}
                  index={i}
                  error={errors.get(i) || {}}
                  showErrors={showErrors}
                  onDragOver={this.onDragOver}
                  onDrop={this.onDrop}
                  onDragStart={this.onDragStart}
                  onChange={this.onChange}
                  onChangeQuestionType={this.onChangeQuestionType}
                  onWeightBlur={() => this.onWeightBlur(question, i)}
                  deleteQuestion={this.deleteQuestion}
                  blink={questionsToBlink.get(i)}
                />
              ))
            )}
          </tbody>
        </table>
        <Button
          variant="outlined"
          className="add-question-button ev-button"
          onClick={this.addQuestion}>
          Add Question
        </Button>
      </div>
    );
  }
}
